'use client';

import { useEffect, useRef } from 'react';
import { trackPaywallViewed } from '@/lib/analytics/paywall';
import { useStockPageAuth } from './StockPageAuthWrapper';

/**
 * Fire once the bottom of Key Ratings rises above this share of the viewport.
 * 1 = the moment the end of Key Ratings scrolls into view; 0.5 = it reaches
 * mid-screen. 0.75 sits between: Key Ratings fully read, Growth just starting.
 */
const TRIGGER_VIEWPORT_SHARE = 0.75;

/**
 * Anonymous visitors see Key Ratings freely. Once they scroll past it, the
 * register modal opens. Closing it (×) sends them back to the top of the
 * page, and scrolling past Key Ratings again reopens it — every time, until
 * they register or sign in.
 */
export default function AnonScrollSignupPrompt({
  fullTicker,
  authModalOpen,
}: {
  fullTicker: string;
  authModalOpen: boolean;
}) {
  const { accessLevel, setAuthMode, setAuthModalOpen } = useStockPageAuth();
  const isAnonymous = accessLevel === 'anonymous';
  /** True while this component's modal is showing (vs. one opened elsewhere). */
  const openedByPromptRef = useRef(false);

  // Modal closed without signing in → back to the top; the scroll listener
  // below stays armed and reopens it on the next pass.
  useEffect(() => {
    if (authModalOpen || !openedByPromptRef.current) return;
    openedByPromptRef.current = false;
    if (isAnonymous) window.scrollTo({ top: 0, behavior: 'instant' });
  }, [authModalOpen, isAnonymous]);

  useEffect(() => {
    if (!isAnonymous || authModalOpen) return;

    const check = () => {
      // Only after the visitor has scrolled: on tall screens the end of Key
      // Ratings can already be in view on load.
      if (window.scrollY <= 0) return;
      const ratings = document.getElementById('section-ratings');
      if (!ratings) return;
      const { bottom } = ratings.getBoundingClientRect();
      if (bottom > window.innerHeight * TRIGGER_VIEWPORT_SHARE) return;

      openedByPromptRef.current = true;
      setAuthMode('register');
      setAuthModalOpen(true);
      trackPaywallViewed({
        surface: 'stock',
        trigger: 'scroll_gate',
        ticker: fullTicker,
        is_authenticated: false,
      });
    };

    // Capture phase catches scrolls of inner containers too, not just window.
    document.addEventListener('scroll', check, { capture: true, passive: true });
    window.addEventListener('resize', check, { passive: true });
    // The page may already be past Key Ratings (restored scroll, #anchor link).
    check();
    return () => {
      document.removeEventListener('scroll', check, true);
      window.removeEventListener('resize', check);
    };
  }, [isAnonymous, authModalOpen, fullTicker, setAuthMode, setAuthModalOpen]);

  return null;
}
